import { useCallback } from "react";

import { useAppDispatch, useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { editProfile, toggleAttendeeInPersonMode } from "../../../store/actions/event/event-actions";
import type { IProfileEditRequest } from "../../../connection/registration";
import { RegFieldsEnum } from "../../../types/working-model";
import { IN_PERSON_NO, IN_PERSON_YES, setInPersonWorking } from "../../../utils/in-person-intent";

// Single write path for the attendee's own in-person toggle: redux plus the persisted profile answer.
const useToggleAttendeeInPersonMode = (): ((nextOn: boolean) => void) => {
	const dispatch = useAppDispatch();

	const eventUuid = useTypedSelector(state => state.LiveEventReducer.eventBundle?.uuid);
	const channel = useTypedSelector(state => state.LiveEventReducer.eventBundle?.channel);
	const blProfileUser = useTypedSelector(state => state.LiveEventReducer.blProfileUser);
	const blProfileToken = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);
	const registrationId = useTypedSelector(state => state.LiveEventReducer.registrationId);

	return useCallback((nextOn: boolean) => {
		dispatch(toggleAttendeeInPersonMode(nextOn));
		setInPersonWorking(eventUuid, nextOn);

		const profileChannelFields = channel !== undefined ? blProfileUser?.profile?.[channel] : undefined;

		// Non-registered viewers have no profile to write to; the redux flag alone is the best we can do.
		if (
			!eventUuid
			|| !channel
			|| !blProfileToken
			|| !registrationId
			|| !blProfileUser?.bl_profile
			|| !profileChannelFields
		) return;

		const profileRequest: IProfileEditRequest = {
			fields: {
				...profileChannelFields,
				[RegFieldsEnum.attend_in_person]: nextOn ? IN_PERSON_YES : IN_PERSON_NO,
			},
			avatar: profileChannelFields?.avatar,
			channel,
			registrationId
		};

		dispatch(editProfile(profileRequest, eventUuid, blProfileToken));
	}, [dispatch, eventUuid, channel, blProfileUser, blProfileToken, registrationId]);
};

export default useToggleAttendeeInPersonMode;
